<template>
  <div class="billing-plan-management-container">
    <div class="header-container">
      <h1>{{ $t('billingPlanManagement.title') }}</h1>
      <button class="btn-add" @click="showAddPlanForm = true">
        <i class="fas fa-plus"></i> {{ $t('billingPlanManagement.addPlan') }}
      </button>
    </div>

    <div v-if="isLoading" class="loading-spinner">
      <i class="fas fa-spinner fa-spin"></i> {{ $t('common.loading') }}
    </div>

    <div v-else-if="billingPlans.length === 0" class="no-data">
      {{ $t('billingPlanManagement.noPlans') }}
    </div>

    <div v-else class="table-responsive">
      <table class="billing-plan-table">
        <thead>
          <tr>
            <th>{{ $t('billingPlanManagement.planName') }}</th>
            <th>{{ $t('billingPlanManagement.lessonPrice') }}</th>
            <th>{{ $t('billingPlanManagement.teacherEarnings') }}</th>
            <th>{{ $t('billingPlanManagement.actions') }}</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="plan in billingPlans" :key="plan.id">
            <td>{{ plan.plan_name }}</td>
            <td>{{ formatCurrency(plan.lesson_price) }}</td>
            <td>{{ formatCurrency(plan.teacher_earnings) }}</td>
            <td class="actions-cell">
              <button class="btn-edit" @click="editPlan(plan)">
                <i class="fas fa-edit"></i> {{ $t('common.edit') }}
              </button>
              <button class="btn-delete" @click="confirmDeletePlan(plan)">
                <i class="fas fa-trash"></i> {{ $t('common.delete') }}
              </button>
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <!-- Add Billing Plan Form -->
    <div class="overlay" v-if="showAddPlanForm">
      <div class="form-popup">
        <h2>{{ $t('billingPlanManagement.addPlan') }}</h2>
        <form @submit.prevent="addBillingPlan">
          <div class="form-group">
            <label class="form-label" for="plan-name">{{ $t('billingPlanManagement.planName') }}</label>
            <input type="text" class="form-control" id="plan-name" v-model="newPlan.plan_name" required>
          </div>
          
          <div class="form-group">
            <label class="form-label" for="lesson-price">{{ $t('billingPlanManagement.lessonPrice') }}</label>
            <input type="number" step="0.01" min="0" class="form-control" id="lesson-price" v-model="newPlan.lesson_price" required>
          </div>
          
          <div class="form-group">
            <label class="form-label" for="teacher-earnings">{{ $t('billingPlanManagement.teacherEarnings') }}</label>
            <input type="number" step="0.01" min="0" class="form-control" id="teacher-earnings" v-model="newPlan.teacher_earnings" required>
          </div>
          
          <div class="form-actions">
            <button type="button" class="btn-cancel" @click="showAddPlanForm = false">
              {{ $t('common.cancel') }}
            </button>
            <button type="submit" class="btn-submit">
              {{ $t('common.save') }}
            </button>
          </div>
        </form>
      </div>
    </div>

    <!-- Edit Billing Plan Form -->
    <div class="overlay" v-if="showEditPlanForm">
      <div class="form-popup">
        <h2>{{ $t('billingPlanManagement.editPlan') }}</h2>
        <form @submit.prevent="updateBillingPlan">
          <div class="form-group">
            <label class="form-label" for="edit-plan-name">{{ $t('billingPlanManagement.planName') }}</label>
            <input type="text" class="form-control" id="edit-plan-name" v-model="editedPlan.plan_name" required>
          </div>
          
          <div class="form-group">
            <label class="form-label" for="edit-lesson-price">{{ $t('billingPlanManagement.lessonPrice') }}</label>
            <input type="number" step="0.01" min="0" class="form-control" id="edit-lesson-price" v-model="editedPlan.lesson_price" required>
          </div>
          
          <div class="form-group">
            <label class="form-label" for="edit-teacher-earnings">{{ $t('billingPlanManagement.teacherEarnings') }}</label>
            <input type="number" step="0.01" min="0" class="form-control" id="edit-teacher-earnings" v-model="editedPlan.teacher_earnings" required>
          </div>
          
          <div class="form-actions">
            <button type="button" class="btn-cancel" @click="showEditPlanForm = false">
              {{ $t('common.cancel') }}
            </button>
            <button type="submit" class="btn-submit">
              {{ $t('common.save') }}
            </button>
          </div>
        </form>
      </div>
    </div>

    <!-- Delete Confirmation Modal -->
    <div class="overlay" v-if="showDeleteConfirmation">
      <div class="confirmation-modal">
        <div class="confirmation-content">
          <h3>{{ $t('billingPlanManagement.confirmDelete') }}</h3>
          <p class="warning-text">{{ $t('billingPlanManagement.deleteWarning', { name: planToDelete?.plan_name }) }}</p>
          
          <div class="confirmation-actions">
            <button class="btn-cancel" @click="showDeleteConfirmation = false">
              {{ $t('common.cancel') }}
            </button>
            <button class="btn-delete" @click="deleteBillingPlan">
              {{ $t('common.delete') }}
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axiosInstance from '@/router/axiosSetup';

export default {
  name: 'BillingPlanManagement',
  data() {
    return {
      billingPlans: [],
      isLoading: true,
      showAddPlanForm: false,
      showEditPlanForm: false,
      showDeleteConfirmation: false,
      newPlan: {
        plan_name: '',
        lesson_price: '',
        teacher_earnings: ''
      },
      editedPlan: {
        id: null,
        plan_name: '',
        lesson_price: '',
        teacher_earnings: ''
      },
      planToDelete: null
    };
  },
  created() {
    this.fetchBillingPlans();
  },
  methods: {
    async fetchBillingPlans() {
        this.isLoading = true;
        try {
            console.log('Fetching billing plans...');
            const response = await axiosInstance.get('/admin/all-billing-plans', {
            headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
            });
            console.log('Raw billing plans data:', response.data);
            // Process the data to ensure numeric values are handled correctly
            this.billingPlans = response.data.map(plan => ({
            ...plan,
            lesson_price: typeof plan.lesson_price === 'string' 
                ? parseFloat(plan.lesson_price.replace(',', '.')) 
                : parseFloat(plan.lesson_price) || 0,
            teacher_earnings: typeof plan.teacher_earnings === 'string' 
                ? parseFloat(plan.teacher_earnings.replace(',', '.')) 
                : parseFloat(plan.teacher_earnings) || 0
            }));
            console.log('Processed billing plans data:', this.billingPlans);
        } catch (error) {
            console.error('Error fetching billing plans:', error);
            this.showNotification('error', this.$t('billingPlanManagement.errorFetchingPlans'));
        } finally {
            this.isLoading = false;
        }
        },
    
    async addBillingPlan() {
        try {
            // Ensure proper number formatting before sending to API
            const planData = {
            plan_name: this.newPlan.plan_name,
            lesson_price: parseFloat(this.newPlan.lesson_price) || 0,
            teacher_earnings: parseFloat(this.newPlan.teacher_earnings) || 0
            };
            
            await axiosInstance.post('/admin/add-billing-plan', planData, {
            headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
            });
            
            // Rest of your code remains the same
            this.showNotification('success', this.$t('billingPlanManagement.planAdded'));
            this.fetchBillingPlans();
            this.showAddPlanForm = false;
            this.resetNewPlan();
        } catch (error) {
            console.error('Error adding billing plan:', error);
            this.showNotification('error', this.$t('billingPlanManagement.errorAddingPlan'));
        }
        },
    
    editPlan(plan) {
      this.editedPlan = { ...plan };
      this.showEditPlanForm = true;
    },
    
    async updateBillingPlan() {
        try {
            // Ensure proper number formatting before sending to API
            const planData = {
            id: this.editedPlan.id,
            plan_name: this.editedPlan.plan_name,
            lesson_price: parseFloat(this.editedPlan.lesson_price) || 0,
            teacher_earnings: parseFloat(this.editedPlan.teacher_earnings) || 0
            };
            
            await axiosInstance.put('/admin/update-billing-plan', planData, {
            headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
            });
            
            // Rest of your code remains the same
            this.showNotification('success', this.$t('billingPlanManagement.planUpdated'));
            this.fetchBillingPlans();
            this.showEditPlanForm = false;
        } catch (error) {
            console.error('Error updating billing plan:', error);
            this.showNotification('error', this.$t('billingPlanManagement.errorUpdatingPlan'));
        }
        },
    
    confirmDeletePlan(plan) {
      this.planToDelete = plan;
      this.showDeleteConfirmation = true;
    },
    
    async deleteBillingPlan() {
      try {
        await axiosInstance.delete(`/admin/delete-billing-plan/${this.planToDelete.id}`, {
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
        });
        
        this.showNotification('success', this.$t('billingPlanManagement.planDeleted'));
        this.fetchBillingPlans();
        this.showDeleteConfirmation = false;
        this.planToDelete = null;
      } catch (error) {
        console.error('Error deleting billing plan:', error);
        this.showNotification('error', this.$t('billingPlanManagement.errorDeletingPlan'));
      }
    },
    
    resetNewPlan() {
      this.newPlan = {
        plan_name: '',
        lesson_price: '',
        teacher_earnings: ''
      };
    },
    
    formatCurrency(value) {
    // Handle null, undefined, or empty string values
    if (value === null || value === undefined || value === '') {
        return '0.00';
    }
    
    // Handle string values that have comma as decimal separator
    if (typeof value === 'string' && value.includes(',')) {
        value = value.replace(',', '.');
    }
    
    // Parse the value and check if it's a valid number
    const parsed = parseFloat(value);
    return isNaN(parsed) ? '0.00' : parsed.toFixed(2);
    },
    
    showNotification(type, message) {
      if (this.$notify) {
        this.$notify({
          group: 'app',
          type: type,
          text: message
        });
      } else {
        alert(message);
      }
    }
  }
};
</script>

<style scoped>
.billing-plan-management-container {
  padding: 20px;
  max-width: 1200px;
  margin: 0 auto;
}

.header-container {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 24px;
}

.header-container h1 {
  margin: 0;
  font-size: 1.8rem;
  color: #333;
}

.btn-add {
  background-color: #4CAF50;
  color: white;
  border: none;
  padding: 10px 16px;
  border-radius: 4px;
  cursor: pointer;
  display: flex;
  align-items: center;
  font-weight: 500;
}

.btn-add i {
  margin-right: 8px;
}

.loading-spinner {
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 200px;
  font-size: 1.2rem;
  color: #666;
}

.loading-spinner i {
  margin-right: 10px;
  font-size: 1.5rem;
}

.no-data {
  text-align: center;
  padding: 40px;
  color: #666;
  background: #f9f9f9;
  border-radius: 8px;
  font-size: 1.1rem;
}

.table-responsive {
  overflow-x: auto;
  background: white;
  border-radius: 8px;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.05);
}

.billing-plan-table {
  width: 100%;
  border-collapse: collapse;
}

.billing-plan-table th, 
.billing-plan-table td {
  padding: 14px 16px;
  text-align: left;
  border-bottom: 1px solid #eee;
}

.billing-plan-table th {
  background-color: #f8f9fa;
  font-weight: 600;
  color: #495057;
}

.billing-plan-table tr:last-child td {
  border-bottom: none;
}

.billing-plan-table tr:hover {
  background-color: #f8fafc;
}

.actions-cell {
  display: flex;
  gap: 8px;
}

.btn-edit {
  background-color: #3498db;
  color: white;
  border: none;
  padding: 6px 12px;
  border-radius: 4px;
  cursor: pointer;
}

.btn-delete {
  background-color: #e74c3c;
  color: white;
  border: none;
  padding: 6px 12px;
  border-radius: 4px;
  cursor: pointer;
}

.overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
  backdrop-filter: blur(3px);
}

.form-popup {
  background: white;
  padding: 24px;
  border-radius: 12px;
  width: 500px;
  max-width: 90%;
  max-height: 90vh;
  overflow-y: auto;
  box-shadow: 0 10px 25px rgba(0,0,0,0.15);
}

.form-popup h2 {
  margin-top: 0;
  margin-bottom: 24px;
  color: #333;
  font-size: 1.5rem;
}

.form-group {
  margin-bottom: 20px;
}

.form-label {
  display: block;
  margin-bottom: 8px;
  font-weight: 500;
  color: #333;
}

.form-control {
  width: 100%;
  padding: 10px 12px;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 1rem;
}

.form-control:focus {
  outline: none;
  border-color: #3498db;
  box-shadow: 0 0 0 2px rgba(52, 152, 219, 0.25);
}

.form-actions {
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  margin-top: 24px;
}

.btn-cancel {
  background-color: #f1f1f1;
  color: #333;
  border: none;
  padding: 10px 16px;
  border-radius: 4px;
  cursor: pointer;
  font-weight: 500;
}

.btn-submit {
  background-color: #4CAF50;
  color: white;
  border: none;
  padding: 10px 16px;
  border-radius: 4px;
  cursor: pointer;
  font-weight: 500;
}

.confirmation-modal {
  background: white;
  padding: 24px;
  border-radius: 12px;
  width: 450px;
  max-width: 90%;
  box-shadow: 0 10px 25px rgba(0,0,0,0.15);
}

.confirmation-content h3 {
  margin-top: 0;
  color: #333;
}

.warning-text {
  color: #e74c3c;
  margin: 16px 0;
}

.confirmation-actions {
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  margin-top: 24px;
}

@media (max-width: 768px) {
  .header-container {
    flex-direction: column;
    align-items: flex-start;
    gap: 16px;
  }
  
  .actions-cell {
    flex-direction: column;
    gap: 8px;
  }
  
  .btn-edit, .btn-delete {
    width: 100%;
  }
}
</style>